import type { UseQueryReturn } from "@pinia/colada";
import type { ComputedRef } from "vue";
import type { Model } from "@/schemas/model.schema";
import { defineQuery, useQuery, useQueryCache } from "@pinia/colada";
import { computed } from "vue";
import { api } from "@/services/api";

/**
 * Query keys factory for models following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const MODEL_QUERY_KEYS = {
  root: ["models"] as const,
};

type ModelsQueryReturn = Omit<UseQueryReturn<Awaited<Model[]>, Error>, "data"> & {
  data: ComputedRef<Model[]>;
};

/**
 * The single cache entry / fetch for `GET /models`, sorted as the whole app
 * expects it:
 * 1. Execution type (non-instance models first)
 * 2. Priority (descending)
 *
 * Both `useModelsQuery` and `useCatalogModelsQuery` share this entry - they
 * only differ in what they hand back (see below).
 */
const useModelsQueryEntry = defineQuery((): ModelsQueryReturn => {
  const { data, ...query } = useQuery({
    key: MODEL_QUERY_KEYS.root,
    query: async () => {
      const { data } = await api.get<Model[]>("/models");

      // Sort models: instance type goes last, then by priority
      return data.sort((a, b) => {
        // Sort by execution type, instance should be at the end
        if (a.execution_type !== b.execution_type) {
          return a.execution_type === "instance" ? 1 : -1;
        }

        // Within same execution type, sort by priority (descending)
        const aPriority = a.priority || 0;
        const bPriority = b.priority || 0;
        return bPriority - aPriority;
      });
    },
    staleTime: 60_000, // 60 seconds - models change infrequently
    gcTime: 10 * 60_000, // 10 minutes - garbage collection time
  });

  return {
    ...query,
    data: computed(() => data.value ?? []),
  };
});

/**
 * Models this organization can actually run (AI-1067).
 *
 * `available === false` means the model only exists with the org's own
 * provider key and that credential is NOT configured - picking it would fail
 * at request time. Every consumer of this hook (playground, knowledge, agent
 * forms, deploys, usage tables) puts models in front of the user as choices,
 * so none of them may receive an unusable one. An older server omits
 * `available`, and those models stay visible.
 *
 * The full catalog, including the unusable entries, is
 * `useCatalogModelsQuery` - used only by the picker that explains WHY they are
 * unusable.
 *
 * ONLY `data` is filtered. Everything else in the returned object comes
 * straight from the shared cache entry, so `state.value.data` (and `refetch`,
 * `asyncStatus`, ...) still see the raw catalog: read the models off `data`.
 *
 * @example
 * ```ts
 * const modelsQuery = useModelsQuery();
 *
 * // In template
 * if (modelsQuery.isLoading.value) return <Spinner />;
 * const models = modelsQuery.data.value;
 * ```
 */
export const useModelsQuery = (): ModelsQueryReturn => {
  const { data, ...query } = useModelsQueryEntry();

  return {
    ...query,
    data: computed(() => data.value.filter((model) => model.available !== false)),
  };
};

/**
 * The unfiltered catalog: same cache entry and same sorting as
 * `useModelsQuery`, but it also returns models the org cannot run yet
 * (`available === false`).
 *
 * Only the LLM picker (`pages/agents/LLMsDropdown`) should use this - it
 * renders those models disabled, says they need the org's own key, and links
 * to Integrations. Anywhere else, an unusable model is just a broken choice.
 */
export const useCatalogModelsQuery = (): ModelsQueryReturn => useModelsQueryEntry();

/**
 * Helper hook to invalidate model queries
 *
 * Use this after mutations that affect models (create, update, delete)
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateModels();
 *
 * async function updateModel(id: string, data: Partial<Model>) {
 *   await api.patch(`/models/${id}`, data);
 *   invalidate.all(); // Refetch all model queries
 * }
 * ```
 */
export function useInvalidateModels() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all model queries
     */
    all: () => queryCache.invalidateQueries({ key: MODEL_QUERY_KEYS.root as any }),
  };
}
